/**
 * The tick pane's RUNG LADDER — what "empty" actually means, named.
 *
 * WHY THIS MODULE EXISTS, and it is not general defensive coding.
 *
 * `StalenessChip`/`feedState` answer ONE question: *is this data recent?* They are correct and
 * they are not sufficient here, because they measure the AGE OF THE NEWEST ROW. Four different
 * failures all present to that chip as either "fresh" or "no data", and two of them are states in
 * which a confident, wrong picture renders:
 *
 *   FRESHNESS ..... "is this data recent?"          — what the chip measures
 *   SUFFICIENCY ... "does it contain what I need?"  — nothing green would ever say
 *
 * A CVD is `Σ(signed size)`. For two days this store held rows that were fresh, growing, and
 * carried neither operand: a data-sourced freshness chip read GREEN while the pane could not draw
 * a single bar. That is the worst state this seat can ship, because every indicator agrees.
 *
 * SEVEN RUNGS. A pane must name WHICH ONE is empty, never merely that it is:
 *
 *   NO_FEED ............ the serve leg is not deployed / did not answer
 *   NO_TICKS_STORED .... endpoint answers, store empty, ingest NOT live      — a fault
 *   WAITING_FIRST_TICK . endpoint answers, store empty, ingest IS live       — ⭐ HEALTHY AND EMPTY
 *   NO_DATA_IN_RANGE ... rows exist, none inside this pane's window
 *   MISSING_FIELDS ..... rows in range, but a derivation operand is absent
 *   FEED_STOPPED ....... rows exist and are NOT INCREASING (rung 7)
 *   OK ................. a series can be drawn — and its delta may legitimately be ZERO
 *
 * THREE RENDER PATHS, NOT TWO: `absent`, `zero` and `broken` are separate. A flat line at zero
 * renders confidently and reads as *"delta is balanced"* — a trading signal the data never made.
 * An amber STALE chip is visibly not-data; A ZERO LINE IS INDISTINGUISHABLE FROM A MEASUREMENT.
 *
 * WAITING_FIRST_TICK IS THE ONE MOST EASILY DROPPED AS PEDANTRY. The ingest RE-SUBSCRIBES on boot,
 * it does not resume; there is no backfill, and the first row will be a tick that has not happened
 * yet. The owner looks immediately after a fix fires and sees nothing — and that is the CORRECT
 * state. Without this rung a working deploy reads as a failed one on the only screen that matters.
 *
 * FEED_STOPPED (rung 7) IS THE ONE WITH NO VISUAL TELL. A frozen table is not a stale feed: at the
 * instant inserts stop, the newest row is SECONDS old, so every freshness chip reads FRESH — and a
 * table that stopped accepting inserts an hour ago is indistinguishable from a feed that is merely
 * slow. It renders as a NORMAL, FRESH, POPULATED panel that is quietly not advancing. It therefore
 * cannot be decided from one sample, and this module refuses to guess from one.
 *
 * ⛔ NEVER wire liveness to process/deploy state. `● Online` measures the CONTAINER, not the
 *    ingest — it reads green over a torn-down subscription. `ingestLive` here must be derived from
 *    the DATA's own timestamps, declared by the endpoint.
 */

/** Rungs, ordered most-broken to least. The order is load-bearing: `rung()` returns the first hit. */
export type TickRung =
  | "NO_FEED"
  | "NO_TICKS_STORED"
  | "WAITING_FIRST_TICK"
  | "NO_DATA_IN_RANGE"
  | "MISSING_FIELDS"
  | "FEED_STOPPED"
  | "OK";

/** Operands a CVD cannot be derived without. `Σ(signed size)` — neither is optional. */
export const CVD_REQUIRED_FIELDS = ["side", "size"] as const;

/**
 * One probe of the serve leg. EVERY count here is as-of `asOfMs` and scoped to `window`/`population`
 * — that is why they travel together in one object rather than as loose arguments.
 *
 * `null` NEVER means zero. It means the endpoint did not report the field, which is a different
 * fact and is graded differently (`Number(null)` is `0`, not `NaN` — a confident zero is exactly
 * the output shape this ladder exists to prevent).
 */
export interface TickProbe {
  /** false = no answer at all. Not an empty answer — no answer. */
  reachable: boolean;
  /** Rows the store holds under this pane's PROJECTION, all time. null = not reported. */
  rowsTotal: number | null;
  /** Rows inside this pane's window under the same projection. null = not reported. */
  rowsInWindow: number | null;
  /**
   * Monotone high-water mark, **TAKEN UNDER `population`** — `max(id) WHERE <population>`, never a
   * bare `max(id)`. Rung 7 needs it because a count can hold still while ids advance.
   *
   * ⛔⛔ THE SCOPING IS NOT PEDANTRY, AND IT IS THE ONLY PART OF THIS INTERFACE A CALLER CAN SATISFY
   * WRONGLY WITHOUT ANY SYMPTOM. BUILDER-bb measured the 2026-08-11 17:00Z cutover: the old and new
   * writers INTERLEAVED FOR 9.1 SECONDS (17:00:07.035 → 17:00:16.126), and 156 content-less rows
   * landed AFTER content had already begun.
   * ⇒ ***A BARE `max(id)` ADVANCES ON ROWS THE PANE CANNOT USE.*** Feed a bare watermark in and
   * `idsRising` goes true off content-less inserts while the content stream is frozen — rung 7
   * fails OPEN, in the safe-looking direction, which is the one way it must never fail.
   *
   * ⭐ GK-q ruled the generation boundary must be ROW-READABLE (`side IS NOT NULL`) rather than an
   * `id <= watermark` marker, ON PRINCIPLE, BEFORE the data existed — and bb's cutover then supplied
   * the evidence: any id watermark would have mis-classified up to 156 rows in either direction.
   * **The same ruling binds here for the same reason: this field is a LIVENESS probe of the
   * POPULATION, not of the TABLE.**
   *
   * ⚠ THIS MODULE CANNOT VERIFY IT. A number carries no provenance, so nothing here can tell a
   * scoped watermark from a bare one — which is why it is stated as a contract obligation at the
   * field rather than asserted in a test. *The check that cannot be written is the one that has to
   * be read.*
   */
  watermark: number | null;
  /** Fields the endpoint declares PRESENT on the rows it served. Presence only — never meaning. */
  fieldsPresent: readonly string[];
  /**
   * Whether the INGEST is live, as declared by the data's own timestamps.
   * ⛔ Must not be sourced from container/deploy liveness.
   */
  ingestLive: boolean;
  /** When this probe was taken. Every number above is as-of this instant. */
  asOfMs: number;
  /**
   * WHERE `asOfMs` CAME FROM — and this field exists because GK-s pointed my own law at my own field.
   *
   * The adapter used to do `payload.served_at ? Date.parse(...) : nowMs`, which collapses TWO
   * DIFFERENT FACTS into one number with no way to tell them apart:
   *   "server"  the response STATED its own instant — authoritative, immune to a wrong client clock
   *   "client"  the response did NOT — we stamped it ourselves, and it is only as good as this browser
   * ⇒ ⭐⭐ ***I BUILT A THREE-STATE CVD REGISTER BECAUSE AN ABSENT VALUE AND A ZERO VALUE ARE
   * DIFFERENT FACTS, AND IN THE SAME FILE COLLAPSED AN ABSENT AS-OF INTO AN INFERRED ONE.*** The
   * as-of is this fleet's most-banked law and it was the field I left un-distinguished.
   * ⚠ It matters live: `served_at` was DROPPED from the ticks payload in `bb700a6` (GK-s confirmed it
   * three ways, a real production regression), so EVERY probe today is silently "client".
   */
  asOfSource: "server" | "client";
  /**
   * The DATA's own newest-row timestamp, as declared by the endpoint. `null` = no row to date.
   *
   * ⛔⛔ THIS FIELD EXISTS BECAUSE ITS ABSENCE WAS A LIVE DEFECT, CAUGHT BY LOOKING AT A RENDER AND
   * BY NOTHING ELSE. The first version of this interface had `asOfMs` and no data timestamp, so the
   * pane's freshness chip was fed the time THE PROBE WAS TAKEN — and rendered a green `ticks 8s`
   * beside a `NO FEED` body. **The chip was measuring when we ASKED, not how fresh the ANSWER was.**
   * ⇒ ***THAT IS `● Online` MEASURES THE CONTAINER, ONE LAYER IN: A LIVENESS SIGNAL SOURCED FROM
   * THE OBSERVER INSTEAD OF THE OBSERVED IS ALWAYS GREEN, BECAUSE THE OBSERVER IS ALWAYS RUNNING.***
   * ⚠ **And the module header had already stated the rule — *"freshness must come from the data's own
   * timestamps"* — thirty lines above an interface that gave a caller no way to supply one.**
   * ⇒ ***A LAW WRITTEN IN A DOC COMMENT AND NOT EXPRESSED IN THE TYPE IS A LAW THE CALLER CANNOT
   * OBEY, AND ITS VIOLATION LOOKS LIKE ORDINARY USE.***
   */
  newestRowMs: number | null;
  /** The window every windowed count above was evaluated over. Rates and counts are meaningless without it. */
  windowS: number;
  /** The projection/filter the counts were taken under, e.g. `schema='trades' AND side IS NOT NULL`. */
  population: string;
}

export interface TickVerdict {
  rung: TickRung;
  /** Short, rendered as the pane's state. Names the rung — never the bare word "empty". */
  headline: string;
  /** One sentence a reader can act on: is this a fault, or an expected transient? */
  detail: string;
  /** Is this a fault someone must act on, or an expected state? Drives amber vs quiet. */
  fault: boolean;
  /** May the pane draw a series at all? Only `OK` may — and a zero delta is then a MEASUREMENT. */
  canRenderSeries: boolean;
  /** Operands the derivation needs and did not get. Empty unless `MISSING_FIELDS`. */
  missingFields: readonly string[];
}

/** The minimum spacing between the two samples rung 7 needs. One reading cannot separate frozen from slow. */
export const FEED_STOPPED_MIN_SPACING_S = 60;

/**
 * Grade one probe, optionally against a PRIOR probe taken earlier.
 *
 * `prev` is what makes rung 7 decidable. Without it this function will NEVER return FEED_STOPPED —
 * it returns OK and says so in `detail`, because ***AN UNDECIDED RUNG MUST NOT BE REPORTED AS A
 * PASSED ONE.*** A caller that never supplies `prev` is blind to rung 7 by construction, which is
 * the honest outcome: the alternative is inventing a verdict from a single sample.
 */
export function tickRung(
  probe: TickProbe,
  prev: TickProbe | null = null,
  required: readonly string[] = CVD_REQUIRED_FIELDS,
): TickVerdict {
  const v = (
    rung: TickRung,
    headline: string,
    detail: string,
    fault: boolean,
    canRenderSeries = false,
    missingFields: readonly string[] = [],
  ): TickVerdict => ({ rung, headline, detail, fault, canRenderSeries, missingFields });

  // 1 — no answer. Distinct from an empty answer: nobody has told us anything.
  if (!probe.reachable) {
    return v("NO_FEED", "NO FEED", "The tick endpoint did not answer. Nothing here is a market observation.", true);
  }

  // A reported-nothing is not a zero. If the endpoint declines to report a count we cannot grade
  // the store, and saying "0 rows" here would manufacture the confident zero this ladder prevents.
  if (probe.rowsTotal === null) {
    return v("NO_FEED", "NO FEED", "The endpoint answered but reported no row count. The store's state is unknown, not empty.", true);
  }

  // 2 / 2b — the store is empty. WHICH of the two depends entirely on whether the ingest is live,
  // and they are opposite verdicts: one is a fault, the other is the expected shape of a good boot.
  if (probe.rowsTotal === 0) {
    return probe.ingestLive
      ? v(
          "WAITING_FIRST_TICK",
          "WAITING FOR FIRST TICK",
          "The ingest is live and the store is still empty. Expected after a boot — it re-subscribes rather than resumes, so there is no backfill and the first row will be a tick that has not happened yet.",
          false,
        )
      : v(
          "NO_TICKS_STORED",
          "NO TICKS STORED",
          "The store is empty and the ingest is not live. Ticks are not reaching the store — someone must act.",
          true,
        );
  }

  // 3 — rows exist, none in this pane's window. A fact about the window, not about the feed.
  if (probe.rowsInWindow === null || probe.rowsInWindow === 0) {
    return v(
      "NO_DATA_IN_RANGE",
      "NO DATA IN RANGE",
      `The store holds rows, none inside the last ${probe.windowS}s under ${probe.population}. Widen the window before reading anything into this.`,
      false,
    );
  }

  // 4 — rows are present and cannot express the derivation. FRESHNESS passes here; SUFFICIENCY does
  // not, and no chip that measures recency would ever say so.
  const missing = required.filter((f) => !probe.fieldsPresent.includes(f));
  if (missing.length > 0) {
    return v(
      "MISSING_FIELDS",
      `MISSING FIELDS: ${missing.join(", ")}`,
      "Rows are arriving and are recent, and they do not carry the operands this series is derived from. Do not derive, do not approximate.",
      true,
      false,
      missing,
    );
  }

  // 5 — rung 7. Two samples, spaced, and BOTH the count and the watermark must advance. A count can
  // hold still while ids advance (deletes) and ids can advance while the count holds still — so
  // either one alone is a weaker test than it looks.
  //
  // ⛔ `rung7Decided` EXISTS BECAUSE THE FIRST VERSION OF THIS FUNCTION DID NOT HAVE IT, AND ITS OWN
  // TEST CAUGHT IT: with a `prev` that was present but NOT comparable (too close together, or a
  // different window), the code fell through to OK and reported *"advancing on both count and
  // watermark"* — a confident claim about a comparison THAT NEVER RAN.
  // ⇒ ***HAVING AN INPUT IS NOT THE SAME AS HAVING USED IT, AND THE GAP BETWEEN THEM IS EXACTLY
  //    WHERE A CHECK REPORTS A PASS IT NEVER PERFORMED*** — the defect this whole module is about,
  //    committed one function deep inside the cure for it.
  let rung7Decided = false;
  if (prev !== null) {
    const spacingS = (probe.asOfMs - prev.asOfMs) / 1000;
    const comparable =
      spacingS >= FEED_STOPPED_MIN_SPACING_S &&
      prev.rowsTotal !== null &&
      prev.watermark !== null &&
      probe.watermark !== null &&
      // ⚠ THE WINDOW LAW: both predicates must be evaluated over ONE SHARED window, or an all-time
      // count paired with a last-minute count silently stops discriminating.
      prev.windowS === probe.windowS &&
      prev.population === probe.population;

    if (comparable) {
      rung7Decided = true;
      const rowsRising = probe.rowsTotal > (prev.rowsTotal as number);
      const idsRising = (probe.watermark as number) > (prev.watermark as number);
      if (!rowsRising || !idsRising) {
        return v(
          "FEED_STOPPED",
          "FEED STOPPED",
          `Rows are present and NOT increasing across ${Math.round(spacingS)}s (count ${rowsRising ? "rose" : "held"}, watermark ${idsRising ? "rose" : "held"}). The newest row is recent, so every freshness chip reads fresh — this is the state with no visual tell.`,
          true,
        );
      }
    }
  }

  // 6 — a series may be drawn. A zero delta from HERE is a measurement; from anywhere above it is a
  // fabrication.
  return v(
    "OK",
    "LIVE",
    rung7Decided
      ? "A series can be drawn, and the store is advancing on both count and watermark."
      : "A series can be drawn. ⚠ Rung 7 (rows present but not advancing) is UNDECIDED — it needs a second probe at least 60s later over the SAME window and population.",
    false,
    true,
  );
}

/**
 * A number that cannot be rendered without its qualifiers.
 *
 * ⭐ THE AS-OF ATTACHES TO THE NUMBER, NOT TO THE PANEL AROUND IT. A StalenessChip qualifies a
 * REGION; a screenshot, a glance, a quote or a memory carries the FIGURE and leaves the region
 * behind. A published row count with its as-of in the body and not in the subject was read ~44x low
 * off an owner's board within two hours — the label does not travel with the pixel.
 */
export interface Figure {
  value: number;
  /** CONTRACTS, ROWS, POINTS — never omitted. A count of rows is not a count of ticks. */
  unit: string;
  asOfMs: number;
  /** The window the value was measured over. A rate without its window is not a rate. */
  windowS: number | null;
  /** The filter the value was measured under. */
  population: string;
  /**
   * Set when the producer cannot defend what this number MEANS (e.g. a field falsified upstream).
   * ⛔ When set, the pane renders the STATE and NOT the figure — a labelled number still renders as
   * a number, and a number gets screenshotted, quoted and remembered without its label.
   * *A quantity you must caveat is a quantity you should not print.*
   */
  untrustedLabel?: string;
}

/** Render a figure with its as-of INLINE, or refuse. There is no path here that emits a bare number. */
export function renderFigure(fig: Figure, nowMs: number): string {
  if (fig.untrustedLabel) return `— (${fig.untrustedLabel})`;
  const ageS = Math.max(0, Math.round((nowMs - fig.asOfMs) / 1000));
  const win = fig.windowS === null ? "" : ` · ${fig.windowS}s window`;
  return `${fig.value.toLocaleString()} ${fig.unit} · as of ${ageS}s ago${win}`;
}
